import { faBell } from "@fortawesome/free-solid-svg-icons/faBell";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { FontSize } from "../../../../components/Text";
import { TaskKind } from "../../../../model/task/shared/TasksKind.enum";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { EntityIcons } from "../../../../utils/GlobalIcons";
import { useDrawersContext } from "../../../tasks/drawers-context";
import {
  SetupCard,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";

const QuickAdd = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  min-height: 2.75rem;
  margin-top: auto;
  padding-inline: ${Spacing.s};
  border-radius: ${Rounded.lg};
  border: 1.5px solid ${({ theme }) => theme.main};
  cursor: text;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.main};
    outline-offset: 2px;
  }
`;

const Placeholder = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.darkerSub};
`;

const ReminderChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.accent};
  font-size: ${FontSize.xs};
  white-space: nowrap;
`;

type DashboardSetupTaskCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupTaskCard: React.FC<DashboardSetupTaskCardProps> = ({
  featured,
  done,
}) => {
  const { t } = useTranslation();
  const { setTaskKind, setTaskCreateVisible } = useDrawersContext();

  return (
    <SetupCard
      $featured={featured}
      $done={done}
      data-testid={TestIDs.dashboard.setup.card("TASK")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.task.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.task.description")}
      </SetupCardDescription>

      <QuickAdd
        type="button"
        onClick={() => {
          setTaskKind(TaskKind.ORPHAN);
          setTaskCreateVisible(true);
        }}
        data-testid={TestIDs.dashboard.setup.cta("TASK")}
      >
        <AtiraIcon icon={EntityIcons.Tasks} color="main" />
        <Placeholder>{t("dashboard.setup.task.placeholder")}</Placeholder>
        <ReminderChip aria-hidden>
          <AtiraIcon icon={faBell} size="xs" />
          {t("dashboard.setup.task.reminder")}
        </ReminderChip>
      </QuickAdd>
    </SetupCard>
  );
};
